import { Row, Col, Form, Message, Loader, useToaster } from 'rsuite'
import qs from 'qs'
import { BsArrowRight } from 'react-icons/bs'
import { useDispatch } from 'react-redux'
import { ActionButton } from '@/components/styles'
import apiWrapper from '@/utils/apiHelpers/apiWrapper'
import useTranslation from '@/hooks/useTranslation'
import LinkButton from '@/components/shared/linkButton'
import ShowVinInfoModal from '@/components/shared/showVinInfoModal'
import { generalConstants } from '@tweddle-se-team/common/constants'
import { useState, useCallback } from 'react'
import cn from 'classnames'
import { useRouter } from 'next/navigation'
import styles from './vinSearch.module.scss'
import { productActions } from '@/store/products'
import { searchActions } from '@/store/search'

const { BRANDS } = generalConstants

const { get } = apiWrapper

const VinInput = ({ className, ...props }) => (
  <Form.Control {...props} className={cn(styles.searchVinInput, className)} name="vin" />
)

export const decodeVin = async ({
  setBrand,
  vin,
  setVin,
  setLoadingVIN,
  setVINError,
  products,
  i18nL10n,
  t,
  router,
  toaster
}) => {
  try {
    const { locale } = i18nL10n

    setVin(vin)
    setVINError(false)
    setLoadingVIN(true)
    let findCriteriaByVinUrl = `publications/findCriteriaByVin/${vin}`
    if (products?.filterBy?.brand) {
      findCriteriaByVinUrl += `/${products.filterBy.brand.toUpperCase()}`
    }

    const result = await apiWrapper.get(findCriteriaByVinUrl) // Uncomment this line for actual API call // Mock API call for testing, to be removed later
    if (!result.success) setVINError(result.response.message)
    else {
      const {
        brand,
        model,
        modelType,
        ngtdModelId,
        katashiki,
        productionDate,
        dateFrom,
        dateTo,
        generationId,
        availableLanguagesCodes
      } = result.response
      const query = {
        brand,
        model,
        modelType,
        ngtdModelId,
        katashiki,
        productionDate,
        dateFrom,
        dateTo,
        generationId,
        hideFooter: true
      }
      setBrand(brand)

      if (locale === 'uk' && !availableLanguagesCodes.includes(locale)) {
        return setVINError(t('unable_to_find_publications'))
      }

      toaster.push(
        <Message type="success" showIcon closable>
          {t('vin_recognized')}
        </Message>
      )

      if (locale && availableLanguagesCodes.includes(locale)) {
        query.language = locale
        router.push(`/publications?${qs.stringify(query)}`)
      } else {
        router.push(`/languages?${qs.stringify(query)}`)
      }
    }
  } catch (e) {
    if (e.response?.status === 400) {
      setVINError(t('vin_incorrect_format'))
    } else if (e.response?.status === 429) setVINError(t('rate_limit_exceeded'))
    else {
      console.error(e)
      setVINError(t('vin_decode_unavailable'))
    }
  } finally {
    setLoadingVIN(false)
  }
}

const VinSearch = ({ i18nL10n, products }) => {
  const dispatch = useDispatch()
  const t = useTranslation()
  const [vin, setVin] = useState('')
  const [showVINInfo, setShowVINInfo] = useState(false)
  const [loadingVIN, setLoadingVIN] = useState(false)
  const [VINError, setVINError] = useState(false)
  const router = useRouter()
  const toaster = useToaster()
  const {
    filterBy: { brand }
  } = products

  const isLexus = brand.toLowerCase() === BRANDS.LEXUS

  const setBrand = useCallback(
    brand => {
      dispatch(productActions.SET_BRAND(brand))
      dispatch(searchActions.SET_BRAND(brand))
    },
    [dispatch]
  )

  const decodeVinFn = useCallback(
    () => decodeVin({ setBrand, vin, setVin, setLoadingVIN, setVINError, products, i18nL10n, t, router, toaster }),
    [setBrand, vin, setVin, setLoadingVIN, setVINError, products, i18nL10n, t, router, toaster]
  )

  return (
    <Row>
      <Col md={22}>
        <ShowVinInfoModal show={showVINInfo} onHide={() => setShowVINInfo(false)} />
        <h4 id="enterVIN" className={cn('mt-20', styles.enterVin)}>
          {t('homepage_enter_vin_number')}
        </h4>
        <Form layout="inline" className={cn('mb-20', styles.vinForm)}>
          <Form.Group>
            <VinInput
              className="mr-10"
              name="VIN"
              placeholder={t('homepage_enter_vin_placeholder')}
              maxLength={17}
              onChange={value => {
                setVin(value)
                setVINError(false)
              }}
              value={vin}
            />
            {loadingVIN && <Loader />}
            {VINError && <span className={cn(styles.vinError)}>{t(VINError)}</span>}
            <Form.HelpText className="mt-10 vinLocation">
              <LinkButton size="md" appearance="link" onClick={() => setShowVINInfo(true)}>
                {t('homepage_where_to_find_vin')}
              </LinkButton>
            </Form.HelpText>
            <ActionButton
              $withIcon={isLexus}
              className="mt-20"
              appearance="primary"
              onClick={decodeVinFn}
              disabled={!vin || vin.length < 17 || loadingVIN}
            >
              {t('look_up_vehicle')} {isLexus && <BsArrowRight size={16} className="fix-btn-icon" />}
            </ActionButton>
          </Form.Group>
        </Form>
      </Col>
      <Col md={2} />
    </Row>
  )
}

export default VinSearch
